<script setup lang="ts">
import { computed } from 'vue';
import Checkbox from '@/components/UI/Checkbox/Checkbox.vue';
import InstallationLocationRowDetails from '@/components/InstallationLocation/InstallationLocationRowDetails.vue';
import { InspectionPointsLocation } from '@/composables/useInstallationLocationsTable';
import { instanceToInstance } from 'class-transformer';

const emits = defineEmits(['update:expanded', 'update-location']);

const props = defineProps<{
  expanded?: string;
  index: number;
}>();

const inspectionPointLocation = defineModel<
  InspectionPointsLocation | undefined
>('inspectionPointLocation');

const isRowExpanded = computed(() => {
  if (!inspectionPointLocation.value) return false;
  return props.expanded === inspectionPointLocation.value.inspectionPoint.id;
});

const toggleExpand = () => {
  if (!inspectionPointLocation.value) return;
  emits(
    'update:expanded',
    isRowExpanded.value
      ? undefined
      : inspectionPointLocation.value.inspectionPoint.id
  );
};

const isInstallationLocation = computed(() => {
  return (
    inspectionPointLocation.value?.inspectionPoint.inspectionData
      .isInstallationLocation ?? false
  );
});

const updateInstallationLocation = (val: boolean) => {
  if (!inspectionPointLocation.value) return;
  inspectionPointLocation.value.inspectionPoint.inspectionData.setIsInstallationLocation(
    val
  );
};
</script>

<template>
  <tr
    class="hover:bg-light-3 hover:dark:bg-dark-2"
    :class="{ 'bg-light-3 dark:bg-dark-2': isRowExpanded }"
    data-testid="installationLocationTableRow"
    @click="toggleExpand()"
  >
    <td class="text-center">
      <em
        :class="[
          'mdi text-xl',
          {
            'mdi-chevron-down': isRowExpanded,
          },
          {
            'mdi-chevron-right': !isRowExpanded,
          },
          { hidden: inspectionPointLocation.groupedDevices.length === 0 },
        ]"
        data-testid="deviceExpansion"
      />
    </td>
    <td class="py-2 px-4">{{ inspectionPointLocation.floor }}</td>
    <td class="py-2 px-4 truncate max-w-[10rem]">
      {{ inspectionPointLocation.roomGroupLabel }}
    </td>
    <td class="py-2 px-4">{{ inspectionPointLocation.statusLabel }}</td>
    <td class="py-2 px-4 text-center">
      <em
        class="mr-3 px-1"
        :class="[
          'mdi',
          inspectionPointLocation.statusIcon,
          inspectionPointLocation.statusType,
          'text-2xl',
        ]"
        data-testid="statusRadioCheck"
      />
    </td>
    <td class="py-2 px-4 text-center">
      {{
        inspectionPointLocation.inspectionPoint.inspectionData.lteReceptionDbm
      }}
    </td>
    <td class="py-2 px-4 text-center" @click.stop>
      <Checkbox
        v-if="inspectionPointLocation.hasResult"
        :checked="isInstallationLocation"
        test-id="installationLocation"
        @update:checked="updateInstallationLocation"
      />
    </td>
  </tr>
  <InstallationLocationRowDetails
    :expanded="isRowExpanded"
    :inspection-point-devices="inspectionPointLocation.groupedDevices"
  />
</template>
